import { CodeIcon } from '@orn/shared-web/icons';
import formatQuantity from '@orn/shared-web/utils/formatQuantity';
import * as React from 'react';

import CopyButton from '../../components/CopyButton';
import IconButton from '../../components/IconButton';
import Table from '../../components/Table';

import { EMPTY_CELL, formatTransactionMoney, formatTransactionType } from './accountDetailView';
import RawJsonBlock from './RawJsonBlock';

import type { AdminGetFinancialAccountDetailQuery } from './__generated__/AccountDetailPanel.gql';

type InvestmentTransaction = NonNullable<
  AdminGetFinancialAccountDetailQuery['adminGetFinancialAccountDetails'][number]['investmentTransactions']
>[number];

const COLUMN_COUNT = 9;
const CELL_CLASS = 'px-2 py-1 text-xs';
const NUMERIC_CELL_CLASS = `${CELL_CLASS} text-right`;
const HEADER_CELL_CLASS = 'px-2 py-1';

interface IInvestmentTransactionsTableProps {
  /** Newest first, as the server ordered them. */
  transactions: readonly InvestmentTransaction[];
}

/**
 * An account's stored investment transactions in the order given, each value
 * as stored — the provider's type words, and an amount that is positive when
 * cash leaves the account — with a per-row toggle for the row's ids and the
 * payload it was recorded from. Scrolls inside its own bounded container so
 * the sticky header has something to stick to.
 */
const InvestmentTransactionsTable = ({ transactions }: IInvestmentTransactionsTableProps) => {
  const [openPayloads, setOpenPayloads] = React.useState<Set<string>>(new Set());

  const togglePayload = (investmentTransactionId: string) => {
    setOpenPayloads((prev) => {
      const next = new Set(prev);
      if (next.has(investmentTransactionId)) {
        next.delete(investmentTransactionId);
      } else {
        next.add(investmentTransactionId);
      }
      return next;
    });
  };

  return (
    <div className="border-border-primary max-h-96 overflow-auto border">
      <Table className="table-fixed">
        <Table.Header sticky>
          <Table.Row>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-24`}>Date</Table.HeaderCell>
            <Table.HeaderCell className={HEADER_CELL_CLASS}>Name</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-40`}>Type</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-32`}>Security</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-28 text-right`}>Qty</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-28 text-right`}>Price</Table.HeaderCell>
            <Table.HeaderCell
              className={`${HEADER_CELL_CLASS} w-32 text-right`}
              title="As stored: positive when cash leaves the account"
            >
              Amount
            </Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-24 text-right`}>Fees</Table.HeaderCell>
            <Table.HeaderCell className={`${HEADER_CELL_CLASS} w-9`} />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {transactions.map((transaction) => {
            const id = transaction.investmentTransactionId;
            const isOpen = openPayloads.has(id);
            return (
              <React.Fragment key={id}>
                <Table.Row>
                  <Table.Cell
                    mono
                    className={CELL_CLASS}
                    title={
                      transaction.transactionDatetime
                        ? new Date(transaction.transactionDatetime).toLocaleString()
                        : undefined
                    }
                  >
                    {transaction.date}
                  </Table.Cell>
                  <Table.Cell className={`${CELL_CLASS} truncate`} title={transaction.name}>
                    {transaction.name}
                  </Table.Cell>
                  <Table.Cell muted className={`${CELL_CLASS} truncate`}>
                    {formatTransactionType(transaction)}
                  </Table.Cell>
                  <Table.Cell
                    mono
                    className={`${CELL_CLASS} truncate`}
                    title={
                      [transaction.tickerSymbol, transaction.securityName].filter(Boolean).join(' · ') || undefined
                    }
                  >
                    {transaction.tickerSymbol ?? transaction.securityName ?? EMPTY_CELL}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {transaction.quantity == null ? EMPTY_CELL : formatQuantity(transaction.quantity)}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {formatTransactionMoney(transaction.price, { ...transaction, isUnitPrice: true })}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {formatTransactionMoney(transaction.amount, transaction)}
                  </Table.Cell>
                  <Table.Cell className={NUMERIC_CELL_CLASS}>
                    {formatTransactionMoney(transaction.fees, transaction)}
                  </Table.Cell>
                  <Table.Cell className={CELL_CLASS}>
                    <IconButton
                      label={isOpen ? 'Hide raw JSON' : 'Show raw JSON'}
                      aria-expanded={isOpen}
                      onClick={() => togglePayload(id)}
                    >
                      <CodeIcon className="size-3.5" />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
                {isOpen && (
                  <Table.Row className="hover:bg-transparent">
                    <Table.Cell className="bg-bg-secondary p-2" colSpan={COLUMN_COUNT}>
                      <div className="flex min-w-0 flex-col gap-2">
                        <div className="text-fg-secondary flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[11px]">
                          <span className="flex items-center gap-1">
                            <span className="text-fg-quaternary">transaction</span>
                            <span className="select-all">{id}</span>
                            <CopyButton
                              value={id}
                              label="Copy transaction id"
                              successMessage="Transaction id copied."
                            />
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="text-fg-quaternary">{transaction.dataSource}</span>
                            <span className="select-all">{transaction.externalId}</span>
                            <CopyButton
                              value={transaction.externalId}
                              label="Copy provider transaction id"
                              successMessage="Provider transaction id copied."
                            />
                          </span>
                          {transaction.securityId && (
                            <span className="flex items-center gap-1">
                              <span className="text-fg-quaternary">security</span>
                              <span className="select-all">{transaction.securityId}</span>
                              {transaction.securityName && <span>{transaction.securityName}</span>}
                              <CopyButton
                                value={transaction.securityId}
                                label="Copy security id"
                                successMessage="Security id copied."
                              />
                            </span>
                          )}
                        </div>
                        <RawJsonBlock title="Transaction JSON" value={transaction.rawJson} />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

export default InvestmentTransactionsTable;
